import React from 'react';
import { Search } from 'lucide-react';

/**
 * Validation master list (left pane, mirroring Discovery's): filter box on
 * top, "Entire BOM" first, then one row per component — name, mono part id,
 * and an honest state chip ("N to fix" / "N ideas" / "fixed" / "ok"). Chips
 * are live: they fall as fixes land, before any re-check.
 */
export interface ValidationListItem {
  key: string;
  name: string;
  partNumber?: string;
  mpn?: string;
  kind: 'bom' | 'component';
  toFix: number;
  hasCritical: boolean;
  ideas: number;
  hadFindings: boolean;
}

interface ValidationListPaneProps {
  items: ValidationListItem[];
  totalComponents: number;
  selectedKey: string | null;
  filter: string;
  onFilterChange: (value: string) => void;
  onSelect: (key: string) => void;
}

const StateChip: React.FC<{ item: ValidationListItem }> = ({ item }) => {
  if (item.toFix > 0) {
    const tone = item.hasCritical ? 'bg-danger-bg text-danger-ink' : 'bg-warn-bg text-warn-ink';
    return <span className={`rounded-[4px] px-s2 py-[1px] text-meta font-semibold ${tone}`}>{item.toFix} to fix</span>;
  }
  if (item.ideas > 0) {
    return (
      <span className="rounded-[4px] bg-ai-chip px-s2 py-[1px] text-meta font-semibold text-robbie">
        {item.ideas} {item.ideas === 1 ? 'idea' : 'ideas'}
      </span>
    );
  }
  if (item.hadFindings) {
    // Covers fixes and skips alike — the card badges carry the exact outcome.
    return <span className="rounded-[4px] bg-success-bg px-s2 py-[1px] text-meta font-semibold text-success-ink">done</span>;
  }
  return <span className="rounded-[4px] bg-neutral-bg px-s2 py-[1px] text-meta font-semibold text-neutral-ink">ok</span>;
};

export const ValidationListPane: React.FC<ValidationListPaneProps> = ({
  items,
  totalComponents,
  selectedKey,
  filter,
  onFilterChange,
  onSelect,
}) => (
  <div className="flex flex-col overflow-hidden border-r border-line max-md:border-r-0">
    <div className="shrink-0 border-b border-line-divider-soft px-[12px] py-[10px]">
      <div className="flex items-center gap-s3 rounded-control border border-line-strong px-s4 py-[7px]">
        <Search className="h-[13px] w-[13px] shrink-0 text-ink-muted" />
        <input
          type="text"
          value={filter}
          onChange={(e) => onFilterChange(e.target.value)}
          placeholder="Filter components…"
          className="w-full border-none bg-transparent font-sans text-body-sm text-ink outline-none placeholder:text-ink-muted"
        />
      </div>
    </div>
    <div className="flex-1 overflow-y-auto">
      {items.map((item) => {
        const active = item.key === selectedKey;
        return (
          <button
            key={item.key}
            type="button"
            onClick={() => onSelect(item.key)}
            className={`block w-full border-b border-line-divider px-[12px] py-[10px] text-left ${
              active ? 'border-l-[2.5px] border-l-action bg-action-bg' : 'hover:bg-surface-hover'
            }`}
          >
            <div
              className={`text-[12.5px] font-semibold leading-[1.35] ${
                active ? 'text-action-deep' : item.kind === 'bom' ? 'text-ink-body' : 'text-ink'
              }`}
            >
              {item.name}
            </div>
            <div className="mt-[3px] flex flex-wrap items-center gap-x-s2 gap-y-[2px]">
              {item.partNumber && <span className="font-mono text-[10.5px] text-ink-muted">{item.partNumber}</span>}
              {item.mpn && <span className="font-mono text-[10.5px] text-ink-muted">MPN {item.mpn}</span>}
              <StateChip item={item} />
            </div>
          </button>
        );
      })}
      <div className="px-[12px] py-[10px] font-mono text-meta text-ink-muted">
        {totalComponents} {totalComponents === 1 ? 'component' : 'components'}
      </div>
    </div>
  </div>
);

export default ValidationListPane;
